/**
 * PhoneSaleForm — R7a (2026-05-18).
 *
 * Reusable form for logging a phone sale. Used by:
 *   - The standalone /sales-activities/phone-sales/new page (contact
 *     not yet known — operator searches by email or member#)
 *   - The Contact Detail "Log Phone Sale" button (contact prefilled
 *     and locked via lockedContactId prop)
 *
 * Per R7a plan decision D2: contact_id is required. Per R7a-A-D4
 * (audit amendment): the create flow must search for an existing
 * contact first; on no match, surface a link to the contact-create
 * flow (inline create is a Phase 2 enhancement).
 *
 * Idempotency: clientId is generated once at mount via
 * crypto.randomUUID() and reused on retry. Without this, double-clicks
 * or network retries would create duplicate rows.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createPhoneSale,
  listSalesPositions,
  type SalesPositionLite,
} from "../../services/salesActivities.service";
import {
  sortSalesPositionsByFirstName,
  displaySalesPositionName,
} from "../../utils/salesPositionDisplay";
import { supabase } from "../../services/supabase";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface ContactResult {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  membership_id: string | null;
}

interface PhoneSaleFormProps {
  /** If provided, contact_id is locked to this value and search UI is hidden. */
  lockedContactId?: string;
  /** Called with the new (or idempotently-matched) phone sale id on success. */
  onSuccess: (activityId: string) => void;
  /** Optional cancel handler — closes modal or navigates away. */
  onCancel?: () => void;
}

// Audit gotcha: PostgREST `.or()` must sanitize special chars before
// composing the filter (see feedback_postgrest_or_sanitize.md).
const sanitizeSearch = (s: string): string => s.replace(/[,()."*\\]/g, "");

const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

const PhoneSaleForm: React.FC<PhoneSaleFormProps> = ({
  lockedContactId,
  onSuccess,
  onCancel,
}) => {
  // clientId minted once at mount so retries are idempotent.
  const clientId = useMemo(() => crypto.randomUUID(), []);

  const [contactId, setContactId] = useState<string | null>(
    lockedContactId ?? null,
  );
  const [contactLabel, setContactLabel] = useState<string>("");
  const [repPositionId, setRepPositionId] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<ContactResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // SA-1.4: crediting organization — affiliate partner OR the "No Reseller"
  // sentinel (the default). The create_phone_sale RPC persists organization_id.
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgLabel, setOrgLabel] = useState<string>("");
  const [orgSearch, setOrgSearch] = useState<string>("");
  const [orgResults, setOrgResults] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);

  // Sales positions for the rep dropdown.
  const { data: positions } = useQuery({
    queryKey: ["phone-sale-form-positions"],
    queryFn: listSalesPositions,
    staleTime: 5 * 60 * 1000,
  });

  // Hydrate the contact label when locked (e.g., from Contact Detail).
  useEffect(() => {
    if (!lockedContactId) {
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, email, membership_id")
        .eq("id", lockedContactId)
        .single();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        setContactLabel(lockedContactId);
        return;
      }
      setContactLabel(formatContactLabel(data as ContactResult));
    })();
    return () => {
      cancelled = true;
    };
  }, [lockedContactId]);

  // SA-1.4: default the crediting org to the "No Reseller" sentinel row.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("is_no_reseller", true)
        .maybeSingle();
      if (cancelled || !data) {
        return;
      }
      setOrgId((prev) => prev ?? data.id);
      setOrgLabel((prev) => prev || data.name);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // SA-1.4: debounced affiliate-partner search (organizations is a few thousand
  // rows, so a name contains-search with a small limit is fine).
  useEffect(() => {
    const term = orgSearch.trim();
    if (term.length < 2) {
      setOrgResults([]);
      return;
    }
    const sanitized = sanitizeSearch(term);
    const timer = setTimeout(async () => {
      setOrgSearchLoading(true);
      const { data } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("is_archived", false)
        .eq("status", "active") // SA-1.4 audit: "active orgs" means status=active, not just un-archived
        .ilike("name", `%${sanitized}%`)
        .order("name")
        .limit(10);
      setOrgResults((data ?? []) as Array<{ id: string; name: string }>);
      setOrgSearchLoading(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [orgSearch]);

  // Debounced contact search by email, member#, or phone (SA-1.5).
  useEffect(() => {
    if (lockedContactId) {
      return;
    }
    const trimmed = searchTerm.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }
    const sanitized = sanitizeSearch(trimmed);
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(null);
      // Smart-detection (mirrors getContacts pattern). Leading-wildcard
      // ILIKE under RLS can drop into the has_org_access per-row plan
      // shape and approach statement-timeout at 631k rows. Phone-sale
      // reps run this during member calls — slow search is visible.
      //   @-in-term → exact email lookup
      //   otherwise → prefix-only membership_id ILIKE (uppercased to
      //   match canonical membership_id storage; trigram index handles
      //   prefix matches cheaply even under RLS).
      if (trimmed.includes("@")) {
        const { data, error } = await supabase
          .from("contacts")
          .select("id, first_name, last_name, email, membership_id")
          .eq("email", trimmed.toLowerCase())
          .limit(10);
        if (error) {
          setSearchError(error.message);
          setSearchLoading(false);
          return;
        }
        setSearchResults((data ?? []) as ContactResult[]);
        setSearchLoading(false);
        return;
      }
      // SA-1.5: a non-email term may be a member number OR a phone number.
      // Run both and merge — member# is a prefix ILIKE (trigram index), phone
      // goes through the indexed search_contacts_by_phone RPC (last-10-digits,
      // format-agnostic) and only fires when the term has a full 10 digits.
      const digits = trimmed.replace(/[^0-9]/g, "");
      const [byMember, byPhone] = await Promise.all([
        supabase
          .from("contacts")
          .select("id, first_name, last_name, email, membership_id")
          .ilike("membership_id", `${sanitized.toUpperCase()}%`)
          .limit(10),
        digits.length >= 10
          ? supabase.rpc("search_contacts_by_phone", { p_phone: trimmed })
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (byMember.error) {
        setSearchError(byMember.error.message);
        setSearchLoading(false);
        return;
      }
      const merged: ContactResult[] = [];
      const seen = new Set<string>();
      for (const c of [
        ...((byMember.data ?? []) as ContactResult[]),
        ...((byPhone.data ?? []) as ContactResult[]),
      ]) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          merged.push(c);
        }
      }
      setSearchResults(merged.slice(0, 10));
      setSearchLoading(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, lockedContactId]);

  const pickContact = (c: ContactResult) => {
    setContactId(c.id);
    setContactLabel(formatContactLabel(c));
    setSearchTerm("");
    setSearchResults([]);
  };

  const clearContact = () => {
    if (lockedContactId) {
      return;
    }
    setContactId(null);
    setContactLabel("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactId) {
      setSubmitError("Pick a contact before submitting.");
      return;
    }
    if (!repPositionId) {
      setSubmitError("Select the rep being credited.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const id = await createPhoneSale({
        repPositionId,
        contactId,
        organizationId: orgId ?? undefined, // SA-1.4: defaults to No Reseller sentinel
        notes: notes.trim() || undefined,
        clientId,
      });
      onSuccess(id);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Could not log phone sale.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(contactId && repPositionId && !submitting);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Contact <span className="text-red-500">*</span>
        </label>
        {contactId ? (
          <div className="mt-1 flex items-center gap-2">
            <span className="rounded bg-blue-50 px-3 py-1 text-sm text-blue-900">
              {contactLabel || contactId}
            </span>
            {!lockedContactId && (
              <button
                type="button"
                onClick={clearContact}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                clear
              </button>
            )}
          </div>
        ) : (
          <div>
            <input
              type="text"
              onKeyDown={preventEnterSubmit}
              placeholder="Search by email, member number, or phone…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
            {searchLoading && (
              <p className="mt-1 text-xs text-gray-500">Searching…</p>
            )}
            {searchError && (
              <p className="mt-1 text-xs text-red-600">{searchError}</p>
            )}
            {!searchLoading &&
              searchTerm.trim().length >= SEARCH_MIN_CHARS &&
              searchResults.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  No contacts match. Create a new contact via{" "}
                  <a
                    href="/contacts"
                    className="text-blue-600 underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Contacts
                  </a>{" "}
                  and come back.
                </p>
              )}
            {searchResults.length > 0 && (
              <ul className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-200 bg-white text-sm">
                {searchResults.map((c) => (
                  <li
                    key={c.id}
                    className="cursor-pointer border-b border-gray-100 px-3 py-2 last:border-0 hover:bg-gray-50"
                    onClick={() => pickContact(c)}
                  >
                    <div className="font-medium">{formatContactLabel(c)}</div>
                    <div className="text-xs text-gray-500">
                      {c.email || "—"} · {c.membership_id || "no member #"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Credited Rep <span className="text-red-500">*</span>
        </label>
        <select
          value={repPositionId}
          onChange={(e) => setRepPositionId(e.target.value)}
          className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Select a rep…</option>
          {sortSalesPositionsByFirstName(positions ?? []).map(
            (p: SalesPositionLite) => (
              <option
                key={p.id}
                value={p.id}
                disabled={!p.is_active}
                title={
                  p.is_active
                    ? undefined
                    : "Inactive sales positions can't be credited on new phone sales."
                }
              >
                {displaySalesPositionName(p, { markInactive: true })}
              </option>
            ),
          )}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Non-admin users can only credit a rep position they're assigned to.
          Admins and managers can credit any active rep.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Crediting Organization
        </label>
        <div className="mt-1">
          <span className="rounded bg-gray-100 px-3 py-1 text-sm text-gray-900">
            {orgLabel || "No Reseller"}
          </span>
        </div>
        <input
          type="text"
          onKeyDown={preventEnterSubmit}
          placeholder="Change — search affiliate partner by name…"
          value={orgSearch}
          onChange={(e) => setOrgSearch(e.target.value)}
          className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        {orgSearchLoading && (
          <p className="mt-1 text-xs text-gray-500">Searching…</p>
        )}
        {orgResults.length > 0 && (
          <ul className="mt-2 max-h-40 overflow-y-auto rounded border border-gray-200 bg-white text-sm">
            {orgResults.map((o) => (
              <li
                key={o.id}
                className="cursor-pointer border-b border-gray-100 px-3 py-2 last:border-0 hover:bg-gray-50"
                onClick={() => {
                  setOrgId(o.id);
                  setOrgLabel(o.name);
                  setOrgSearch("");
                  setOrgResults([]);
                }}
              >
                {o.name}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Defaults to <strong>No Reseller</strong>. If the member signed up
          through an affiliate partner, search and select it.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="Optional — context for the sale, follow-up reminder, etc."
        />
      </div>

      {submitError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {submitError}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? "Logging…" : "Log Phone Sale"}
        </button>
      </div>
    </form>
  );
};

const formatContactLabel = (c: ContactResult): string => {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || c.membership_id || c.id;
};

export default PhoneSaleForm;
